import { ScreeningRecord, ScreeningResult } from "../models/Interfaces";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import "./recordCard.css";
import { useNavigate } from "react-router";
import { ErrorModal } from "./modals/ErrorModal";
import { getDateTime } from "../utils/data";
import { ModalV2, ModalV2BackButton, ModalV2Body, ModalV2Header } from "./modals/ModalV2";
import MobileFooter from "./layout/MobileFooter";
import ScreeningEventCard from "./ScreeningEventCard";
import ScreeningFaq from "../features/screening/ScreeningFaq";

interface ScreeningResultCardProps {
  screening?: ScreeningRecord;
  screeningResult?: ScreeningResult;
}

// Virus images
const lead = "/assets/images/Icons_Screening_Lead.png";

export default function ScreeningResultCard({
  screening,
  screeningResult,
}: ScreeningResultCardProps) {
  const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const emptyCard = (screeningResult?.events ?? []).length < 1;
  const type = emptyCard ? "empty" : screeningResult?.type.toLowerCase();
  const modalRef = useRef<HTMLDialogElement>(null);

  const getBadgeText = () => {
    if (emptyCard) {
      return t("screening.records_status_empty");
    }

    return t("screening.records_status", { context: type });
  };

  return (
    <>
      {modalIsOpen && (
        <ErrorModal
          title={t("screening.records_no_events_header")}
          onCloseModal={() => setModalIsOpen(false)}
        >
          <p style={{ whiteSpace: "pre-wrap" }}>
            {t("screening.records_no_events_message")}
            <a
              href="https://nj.gov/health/childhood-lead"
              target="_blank"
              className="link link-primary"
            >
              nj.gov/health/childhood-lead
            </a>
            .
          </p>
        </ErrorModal>
      )}
      {screening && (
        <ModalV2 ref={modalRef} modalBoxClassName="bg-info dark:bg-secondary">
          <ModalV2Header className="flex items-center gap-2 py-2">
            <ModalV2BackButton />
            <p className="font-semibold text-lg">
              {screening.firstName} {screening.lastName}
            </p>
          </ModalV2Header>
          <ModalV2Body className="pt-2 bg-info dark:bg-secondary">
            <p className="text-xl text-center font-bold pt-2">
              {t("screening.lead_screening_test")}
            </p>
            {screeningResult?.events.map((e, index) => (
              <ScreeningEventCard key={index} screeningEvent={e} />
            ))}
            <ScreeningFaq screening={screening} />
          </ModalV2Body>
        </ModalV2>
      )}
      <div
        onClick={() => {
          if (!screening) {
            setModalIsOpen(true);
          } else {
            modalRef.current?.showModal();
          }
        }}
        className="cursor-pointer"
      >
        <div className="card text-base-content card-side bg-base-100 overflow-hidden border-0">
          <div className="flex flex-1">
            <div
              className={`t sideways-writing text-center text-nowrap uppercase py-1 px-2 font-bold self-stretch flex items-center justify-center  ${
                emptyCard ? "bg-info text-info-content" : "bg-accent text-accent-content"
              }`}
            >
              {getBadgeText()}
            </div>
            <div className="border-2 border-l-0 border-r-0 border-info flex item-center dark:border-base-100 justify-center w-full">
              <div className="card-body py-2">
                <div role="paragraph" className="card-title" data-testid="record-card-title">
                  {t("screening.lead_screening_test")}
                </div>
                {emptyCard ? (
                  <p className="">&nbsp;</p>
                ) : (
                  <>
                    <div className="grid grid-cols-[min-content_1fr]">
                      <span className="text-primary mr-2">{t("screening.records_tested")}</span>
                      <span className="font-semibold">
                        {screeningResult?.latestTestDate
                          ? getDateTime(screeningResult.latestTestDate).toLocaleString()
                          : ""}
                      </span>
                    </div>
                  </>
                )}
                <div
                  role="paragraph"
                  className={`badge badge-info capitalize`}
                  data-testid="record-card-description"
                >
                  {screeningResult?.events?.length || "No"} {t("screening.records_test")}
                  {(screeningResult?.events?.length || 0) !== 1 ? `s` : ""}
                </div>
              </div>
            </div>
            <div className="flex bg-info px-2">
              <div
                className="my-auto w-10 h-10 bg-cover"
                style={{ backgroundImage: `url(${lead})` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
